import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import MapboxMap from './MapboxMap';
import { calculatePrincipalOrientation } from './pcaUtils';
import PCASettingsPanel from './PCASettingsPanel';
import settingsManager from '../utils/settingsManager';
import * as turf from '@turf/turf';

function getCentroid(coords) {
  // Simple centroid for polygon ring
  const n = coords.length;
  const sum = coords.reduce((acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat], [0, 0]);
  return [sum[0] / n, sum[1] / n];
}

function getAxisLine(centroid, angleDeg, length = 0.002) {
  // FIXED: Use the same coordinate transformation pipeline as buildings for perfect alignment
  
  // 1. Convert centroid to meters (same as buildings)
  const centroidMeters = lngLatToMeters(centroid, centroid);
  
  // 2. Create horizontal line in meters coordinate system
  const lengthMeters = length * 111320; // Convert degrees to approximate meters
  const halfLength = lengthMeters / 2;
  const startPointMeters = [-halfLength, 0];
  const endPointMeters = [halfLength, 0];
  
  // 3. Rotate using the same rotatePtM function as buildings
  function rotatePtM([x, y], angleDeg) {
    const angleRad = angleDeg * Math.PI / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return [x * cos - y * sin, x * sin + y * cos];
  }
  
  const rotatedStartMeters = rotatePtM(startPointMeters, angleDeg);
  const rotatedEndMeters = rotatePtM(endPointMeters, angleDeg);
  
  // 4. Translate to centroid position in meters
  const finalStartMeters = [
    centroidMeters[0] + rotatedStartMeters[0],
    centroidMeters[1] + rotatedStartMeters[1]
  ];
  const finalEndMeters = [
    centroidMeters[0] + rotatedEndMeters[0],
    centroidMeters[1] + rotatedEndMeters[1]
  ];
  
  // 5. Convert back to lng/lat using the same function as buildings
  const startLngLat = metersToLngLat(finalStartMeters, centroid);
  const endLngLat = metersToLngLat(finalEndMeters, centroid);
  
  return [startLngLat, endLngLat];
}

// Helper: create a rectangle polygon centered at center, rotated by angleDeg
function createRotatedRectangle(center, width, height, angleDeg) {
  const angleRad = angleDeg * Math.PI / 180;
  const dx = width / 2;
  const dy = height / 2;
  // Rectangle corners before rotation (relative to center)
  const corners = [
    [-dx, -dy],
    [dx, -dy],
    [dx, dy],
    [-dx, dy],
    [-dx, -dy]
  ];
  // Rotate and translate corners
  return corners.map(([x, y]) => {
    const xr = x * Math.cos(angleRad) - y * Math.sin(angleRad);
    const yr = x * Math.sin(angleRad) + y * Math.cos(angleRad);
    return [center[0] + xr, center[1] + yr];
  });
}

// --- Turf-based buildable area (individual setback per edge) ---
function getInsetPolygon(parcelCoords, setbackDistances) {
  if (!parcelCoords || parcelCoords.length < 4 || !setbackDistances || setbackDistances.length !== parcelCoords.length - 1) return null;
  
  try {
    const parcelPolygon = turf.polygon([parcelCoords]);
    
    // Check if all setbacks are the same - if so, use simple buffer
    const uniqueSetbacks = [...new Set(setbackDistances)];
    if (uniqueSetbacks.length === 1) {
      const setbackFt = uniqueSetbacks[0];
      const setbackMeters = setbackFt * 0.3048;
      
      if (setbackMeters <= 0) return null;
      
      const buffered = turf.buffer(parcelPolygon, -setbackMeters, { units: 'meters' });
      const coords = buffered?.geometry?.coordinates?.[0];
      return coords && coords.length > 2 ? coords : null;
    }
    
    // For individual setbacks, use per-edge processing
    try {
      const setbacksMeters = setbackDistances.map(ft => ft * 0.3048);
      
      // Create new polygon coordinates by moving each edge inward
      const newCoords = [];
      const numEdges = parcelCoords.length - 1; // Last coord is same as first
      
      for (let i = 0; i < numEdges; i++) {
        const currentEdgeStart = parcelCoords[i];
        const currentEdgeEnd = parcelCoords[i + 1];
        const setbackMeters = setbacksMeters[i] || 0;
        
        // Skip if no setback for this edge
        if (setbackMeters <= 0) {
          if (i === 0) newCoords.push(currentEdgeStart);
          continue;
        }
        
        // Calculate edge vector and inward normal
        const edgeVector = [
          currentEdgeEnd[0] - currentEdgeStart[0],
          currentEdgeEnd[1] - currentEdgeStart[1]
        ];
        const edgeLength = Math.sqrt(edgeVector[0] ** 2 + edgeVector[1] ** 2);
        
        if (edgeLength === 0) {
          if (i === 0) newCoords.push(currentEdgeStart);
          continue;
        }
        
        // Normalized edge vector
        const edgeUnit = [edgeVector[0] / edgeLength, edgeVector[1] / edgeLength];
        
        // Perpendicular vector (rotate 90 degrees counterclockwise)
        const perpendicular = [-edgeUnit[1], edgeUnit[0]];
        
        // Determine if perpendicular points inward
        const parcelCentroid = [
          parcelCoords.reduce((sum, coord) => sum + coord[0], 0) / parcelCoords.length,
          parcelCoords.reduce((sum, coord) => sum + coord[1], 0) / parcelCoords.length
        ];
        
        const edgeMidpoint = [
          (currentEdgeStart[0] + currentEdgeEnd[0]) / 2,
          (currentEdgeStart[1] + currentEdgeEnd[1]) / 2
        ];
        
        const toCentroid = [
          parcelCentroid[0] - edgeMidpoint[0],
          parcelCentroid[1] - edgeMidpoint[1]
        ];
        
        // Check if perpendicular points toward centroid (inward)
        const dotProduct = perpendicular[0] * toCentroid[0] + perpendicular[1] * toCentroid[1];
        const inwardNormal = dotProduct > 0 ? perpendicular : [-perpendicular[0], -perpendicular[1]];
        
        // Convert setback distance to coordinate offset
        const metersToDegreesLat = 1 / 111320;
        const metersToDegreesLng = 1 / (111320 * Math.cos(edgeMidpoint[1] * Math.PI / 180));
        
        const offsetLng = inwardNormal[0] * setbackMeters * metersToDegreesLng;
        const offsetLat = inwardNormal[1] * setbackMeters * metersToDegreesLat;
        
        // Move edge inward by setback distance
        const newEdgeStart = [
          currentEdgeStart[0] + offsetLng,
          currentEdgeStart[1] + offsetLat
        ];
        const newEdgeEnd = [
          currentEdgeEnd[0] + offsetLng,
          currentEdgeEnd[1] + offsetLat
        ];
        
        // Add the moved edge start point
        if (i === 0) {
          newCoords.push(newEdgeStart);
        }
        
        // For adjacent edges, we need to find intersection point
        if (i < numEdges - 1) {
          // Get next edge info
          const nextEdgeStart = parcelCoords[i + 1];
          const nextEdgeEnd = parcelCoords[(i + 2) % parcelCoords.length];
          const nextSetbackMeters = setbacksMeters[i + 1] || 0;
          
          if (nextSetbackMeters > 0) {
            // Calculate next edge's inward normal and offset
            const nextEdgeVector = [
              nextEdgeEnd[0] - nextEdgeStart[0],
              nextEdgeEnd[1] - nextEdgeStart[1]
            ];
            const nextEdgeLength = Math.sqrt(nextEdgeVector[0] ** 2 + nextEdgeVector[1] ** 2);
            
            if (nextEdgeLength > 0) {
              const nextEdgeUnit = [nextEdgeVector[0] / nextEdgeLength, nextEdgeVector[1] / nextEdgeLength];
              const nextPerpendicular = [-nextEdgeUnit[1], nextEdgeUnit[0]];
              
              const nextEdgeMidpoint = [
                (nextEdgeStart[0] + nextEdgeEnd[0]) / 2,
                (nextEdgeStart[1] + nextEdgeEnd[1]) / 2
              ];
              
              const nextToCentroid = [
                parcelCentroid[0] - nextEdgeMidpoint[0],
                parcelCentroid[1] - nextEdgeMidpoint[1]
              ];
              
              const nextDotProduct = nextPerpendicular[0] * nextToCentroid[0] + nextPerpendicular[1] * nextToCentroid[1];
              const nextInwardNormal = nextDotProduct > 0 ? nextPerpendicular : [-nextPerpendicular[0], -nextPerpendicular[1]];
              
              const nextMetersToDegreesLng = 1 / (111320 * Math.cos(nextEdgeMidpoint[1] * Math.PI / 180));
              const nextOffsetLng = nextInwardNormal[0] * nextSetbackMeters * nextMetersToDegreesLng;
              const nextOffsetLat = nextInwardNormal[1] * nextSetbackMeters * metersToDegreesLat;
              
              const nextNewEdgeStart = [
                nextEdgeStart[0] + nextOffsetLng,
                nextEdgeStart[1] + nextOffsetLat
              ];
              const nextNewEdgeEnd = [
                nextEdgeEnd[0] + nextOffsetLng,
                nextEdgeEnd[1] + nextOffsetLat
              ];
              
              // Find intersection of current moved edge and next moved edge
              const intersection = findLineIntersection(
                [newEdgeStart, newEdgeEnd],
                [nextNewEdgeStart, nextNewEdgeEnd]
              );
              
              newCoords.push(intersection);
            } else {
              newCoords.push(newEdgeEnd);
            }
          } else {
            newCoords.push(newEdgeEnd);
          }
        } else {
          // Last edge - find intersection with first edge
          if (setbacksMeters[0] > 0 && newCoords.length > 0) {
            // Find intersection with first edge
            const firstEdgeStart = parcelCoords[0];
            const firstEdgeEnd = parcelCoords[1];
            const firstSetbackMeters = setbacksMeters[0];
            
            // Calculate first edge's moved position (same logic as above)
            const firstEdgeVector = [firstEdgeEnd[0] - firstEdgeStart[0], firstEdgeEnd[1] - firstEdgeStart[1]];
            const firstEdgeLength = Math.sqrt(firstEdgeVector[0] ** 2 + firstEdgeVector[1] ** 2);
            
            if (firstEdgeLength > 0) {
              const firstEdgeUnit = [firstEdgeVector[0] / firstEdgeLength, firstEdgeVector[1] / firstEdgeLength];
              const firstPerpendicular = [-firstEdgeUnit[1], firstEdgeUnit[0]];
              
              const firstEdgeMidpoint = [
                (firstEdgeStart[0] + firstEdgeEnd[0]) / 2,
                (firstEdgeStart[1] + firstEdgeEnd[1]) / 2
              ];
              
              const firstToCentroid = [
                parcelCentroid[0] - firstEdgeMidpoint[0],
                parcelCentroid[1] - firstEdgeMidpoint[1]
              ];
              
              const firstDotProduct = firstPerpendicular[0] * firstToCentroid[0] + firstPerpendicular[1] * firstToCentroid[1];
              const firstInwardNormal = firstDotProduct > 0 ? firstPerpendicular : [-firstPerpendicular[0], -firstPerpendicular[1]];
              
              const firstMetersToDegreesLng = 1 / (111320 * Math.cos(firstEdgeMidpoint[1] * Math.PI / 180));
              const firstOffsetLng = firstInwardNormal[0] * firstSetbackMeters * firstMetersToDegreesLng;
              const firstOffsetLat = firstInwardNormal[1] * firstSetbackMeters * metersToDegreesLat;
              
              const firstNewEdgeStart = [
                firstEdgeStart[0] + firstOffsetLng,
                firstEdgeStart[1] + firstOffsetLat
              ];
              const firstNewEdgeEnd = [
                firstEdgeEnd[0] + firstOffsetLng,
                firstEdgeEnd[1] + firstOffsetLat
              ];
              
              // Find intersection of last moved edge and first moved edge
              const intersection = findLineIntersection(
                [newEdgeStart, newEdgeEnd],
                [firstNewEdgeStart, firstNewEdgeEnd]
              );
              
              newCoords.push(intersection);
              
              // Update first coordinate to match the intersection
              newCoords[0] = intersection;
            }
          } else {
            newCoords.push(newEdgeEnd);
          }
        }
        
      }
      
      // Close the polygon
      if (newCoords.length > 0) {
        newCoords.push(newCoords[0]);
      }
      
      if (newCoords.length > 3) {
        return newCoords;
      }
    } catch (error) {
      // Individual setback processing failed, fall through to fallback
    }
    
    // Fallback to minimum setback if individual processing fails
    const minSetbackFt = Math.min(...setbackDistances);
    const minSetbackMeters = minSetbackFt * 0.3048;
    
    if (minSetbackMeters <= 0) return null;
    
    const buffered = turf.buffer(parcelPolygon, -minSetbackMeters, { units: 'meters' });
    const coords = buffered?.geometry?.coordinates?.[0];
    
    if (coords && coords.length > 2) {
      return coords;
    }
  } catch (error) {
    // Processing failed
  }
  
  // Fallback to minimum setback method if individual processing fails
  const minSetbackFt = Math.min(...setbackDistances);
  if (minSetbackFt <= 0) return null;
  const minSetbackMeters = minSetbackFt * 0.3048;
  
  try {
    const poly = turf.polygon([[...parcelCoords]]);
    const buffered = turf.buffer(poly, -minSetbackMeters, { units: 'meters' });
    const coords = buffered?.geometry?.coordinates?.[0];
    return coords && coords.length > 2 ? coords : null;
  } catch (error) {
    console.error('❌ Both individual and fallback setback methods failed:', error);
    return null;
  }
}

// Helper function to find intersection of two lines
function findLineIntersection(line1, line2) {
  const [p1, p2] = line1;
  const [p3, p4] = line2;
  
  const x1 = p1[0], y1 = p1[1];
  const x2 = p2[0], y2 = p2[1];
  const x3 = p3[0], y3 = p3[1];
  const x4 = p4[0], y4 = p4[1];
  
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  if (Math.abs(denominator) < 1e-10) {
    // Lines are parallel, return midpoint between closest endpoints
    return [(p2[0] + p3[0]) / 2, (p2[1] + p3[1]) / 2];
  }
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
  
  return [
    x1 + t * (x2 - x1),
    y1 + t * (y2 - y1)
  ];
}

// Helper function to get edge direction label
function getEdgeDirectionLabel(start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  
  // Calculate angle in degrees (0° = East, 90° = North)
  let angle = Math.atan2(dy, dx) * 180 / Math.PI;
  if (angle < 0) angle += 360;
  
  // Determine primary direction
  if (angle >= 315 || angle < 45) return 'East';
  if (angle >= 45 && angle < 135) return 'North';
  if (angle >= 135 && angle < 225) return 'West';
  if (angle >= 225 && angle < 315) return 'South';
  
  return `${angle.toFixed(0)}°`;
}

const LAYOUT_STRATEGIES = [
  { id: 'smart-hybrid', name: 'Auto-Select Best Fit' },
  { id: 'parallel-to-orientation', name: 'Parallel to Orientation' },
  { id: 'perpendicular-to-orientation', name: 'Perpendicular to Orientation' },
  // Legacy strategies kept for backwards compatibility
  { id: 'vertical-packing', name: 'Vertical Packing (Legacy)', hidden: true },
  { id: 'horizontal-packing', name: 'Horizontal Packing (Legacy)', hidden: true },
  { id: 'rotated-vertical-packing', name: 'Rotated Vertical (Legacy)', hidden: true },
  { id: 'rotated-horizontal-packing', name: 'Rotated Horizontal (Legacy)', hidden: true }
];

function rotatePoint([x, y], [cx, cy], angleDeg) {
  const angleRad = angleDeg * Math.PI / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = x - cx;
  const dy = y - cy;
  return [
    cx + dx * cos - dy * sin,
    cy + dx * sin + dy * cos
  ];
}

function rotatePolygon(coords, center, angleDeg) {
  return coords.map(pt => rotatePoint(pt, center, angleDeg));
}

function lngLatToMeters([lng, lat], origin) {
  // Approximate conversion (good enough for parcel-scale analysis)
  const R = 6378137;
  const dLng = (lng - origin[0]) * Math.PI / 180;
  const dLat = (lat - origin[1]) * Math.PI / 180;
  const x = dLng * R * Math.cos(origin[1] * Math.PI / 180);
  const y = dLat * R;
  return [x, y];
}

function metersToLngLat([x, y], origin) {
  const R = 6378137;
  const lng = origin[0] + (x / (R * Math.cos(origin[1] * Math.PI / 180))) * 180 / Math.PI;
  const lat = origin[1] + (y / R) * 180 / Math.PI;
  return [lng, lat];
}

// PRECISION MODE: Generate boxes centered on the orientation line
function generatePrecisionLayout(buildableAreaCoords, boxWidthFt, boxLengthFt, orientationAngle, precisionBoxCount, precisionOffset, precisionOffsetY, maxBoxes) {
  if (!buildableAreaCoords || buildableAreaCoords.length < 4) return { buildPaths: [], boxes: [], layoutAngle: orientationAngle };
  
  console.log(`🎯 PRECISION MODE: angle=${orientationAngle?.toFixed(1)}°, xOffset=${precisionOffset}ft (perpendicular), yOffset=${precisionOffsetY}ft (along line), boxCount=${precisionBoxCount || 'auto'}`);
  
  // 1. Calculate centroid and convert to meters
  const centroid = [
    buildableAreaCoords.reduce((a, b) => a + b[0], 0) / buildableAreaCoords.length,
    buildableAreaCoords.reduce((a, b) => a + b[1], 0) / buildableAreaCoords.length
  ];
  const polyMeters = buildableAreaCoords.map(pt => lngLatToMeters(pt, centroid));
  
  // 2. Rotate polygon to align with orientation angle
  function rotatePtM([x, y], angleDeg) {
    const angleRad = angleDeg * Math.PI / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return [x * cos - y * sin, x * sin + y * cos];
  }
  
  const rotatedPoly = polyMeters.map(pt => rotatePtM(pt, -orientationAngle));
  
  // 3. Find the extent along the orientation line (X axis after rotation)
  const xs = rotatedPoly.map(c => c[0]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const lineLength = maxX - minX;
  
  // 4. Box dimensions in meters
  // CRITICAL: Boxes are placed SIDE-BY-SIDE (length-axis adjacent)
  // - Box WIDTH (12.9ft) is the step along the orientation line
  // - Box LENGTH (68ft) is perpendicular to the line
  const boxLengthM = boxLengthFt * 0.3048;  // 68ft - perpendicular to line
  const boxWidthM = boxWidthFt * 0.3048;    // 12.9ft - along the line (step size)
  
  // 5. Calculate how many boxes can fit (stepping by WIDTH, not length)
  const maxPossibleBoxes = Math.floor(lineLength / boxWidthM);
  const actualBoxCount = precisionBoxCount ? Math.min(precisionBoxCount, maxPossibleBoxes, maxBoxes) : Math.min(maxPossibleBoxes, maxBoxes);
  
  console.log(`🎯 PRECISION: lineLength=${(lineLength/0.3048).toFixed(0)}ft, boxWidth=${boxWidthFt}ft, maxBoxes=${maxPossibleBoxes}`);
  console.log(`🎯 PRECISION: minX=${(minX/0.3048).toFixed(0)}ft, maxX=${(maxX/0.3048).toFixed(0)}ft`);
  
  if (actualBoxCount < 1) {
    console.log('⚠️ PRECISION: No space for boxes');
    return { buildPaths: [], boxes: [], layoutAngle: orientationAngle };
  }
  
  // 6. Calculate starting position - center boxes on the ACTUAL center of the buildable extent
  // NOT on the centroid (which is X=0), but on the midpoint of minX to maxX
  const lineCenterX = (minX + maxX) / 2;  // Actual center of the buildable span
  const totalBuildingWidth = actualBoxCount * boxWidthM;  // Total width along line
  const startX = lineCenterX - totalBuildingWidth / 2;  // Center on actual line center
  
  console.log(`🎯 PRECISION: lineCenterX=${(lineCenterX/0.3048).toFixed(0)}ft, startX=${(startX/0.3048).toFixed(0)}ft`);
  
  // 7. Apply offsets: X perpendicular to line, Y along the line
  const offsetXM = precisionOffset * 0.3048;      // X offset (perpendicular to orientation line)
  const offsetYM = precisionOffsetY * 0.3048;     // Y offset (along the orientation line)
  const adjustedStartX = startX + offsetYM;       // Y shifts building along the line
  const centerY = offsetXM;                        // X shifts building perpendicular to line
  
  console.log(`🎯 PRECISION: xOffset=${precisionOffset}ft (perpendicular), yOffset=${precisionOffsetY}ft (along line), adjustedStartX=${(adjustedStartX/0.3048).toFixed(0)}ft`);
  
  // 8. Generate boxes - stepping by WIDTH along the line
  const buildPaths = [];
  const boxes = [];
  const buildingId = 1;
  const buildPathCoords = [];
  
  for (let i = 0; i < actualBoxCount; i++) {
    // Step by boxWidthM along the line (X axis), using adjusted start position
    const boxCenterX = adjustedStartX + (i + 0.5) * boxWidthM;
    const boxCenterY = centerY;
    
    // Box corners in rotated coordinate system
    // WIDTH (12.9ft) along X (the line), LENGTH (68ft) along Y (perpendicular)
    const halfWidth = boxWidthM / 2;   // Half of 12.9ft along line
    const halfLength = boxLengthM / 2; // Half of 68ft perpendicular to line
    const localCorners = [
      [boxCenterX - halfWidth, boxCenterY - halfLength],
      [boxCenterX + halfWidth, boxCenterY - halfLength],
      [boxCenterX + halfWidth, boxCenterY + halfLength],
      [boxCenterX - halfWidth, boxCenterY + halfLength],
      [boxCenterX - halfWidth, boxCenterY - halfLength]  // Close polygon
    ];
    
    // Rotate back to world coordinates and convert to lng/lat
    const worldCorners = localCorners.map(pt => {
      const rotatedBack = rotatePtM(pt, orientationAngle);
      return metersToLngLat(rotatedBack, centroid);
    });
    
    // Store first box's first corner and last box's last corner for buildPath
    if (i === 0) {
      buildPathCoords.push(worldCorners[0], worldCorners[3]);
    }
    if (i === actualBoxCount - 1) {
      buildPathCoords.push(worldCorners[2], worldCorners[1]);
    }
    
    boxes.push({
      id: `box-${buildingId}-${i}`,
      buildPathId: buildingId,
      corners: worldCorners.slice(0, 4),
      center: metersToLngLat(rotatePtM([boxCenterX, boxCenterY], orientationAngle), centroid),
      widthFt: boxWidthFt,
      lengthFt: boxLengthFt,
      slotIndex: i
    });
  }
  
  // Create building path
  if (boxes.length > 0) {
    buildPaths.push({
      id: buildingId,
      coordinates: buildPathCoords,
      angle: orientationAngle,
      boxCount: boxes.length
    });
  }
  
  console.log(`✅ PRECISION: Generated ${boxes.length} boxes with X offset=${precisionOffset}ft, Y offset=${precisionOffsetY}ft`);
  
  return {
    buildPaths,
    boxes,
    layoutAngle: orientationAngle,
    totalBoxes: boxes.length,
    strategyUsed: 'precision',
    angleUsed: orientationAngle
  };
}

function generatePCALayout(strategy, buildableAreaCoords, boxWidthFt, boxLengthFt, buildingPaddingFt = 0, orientationAngle = 0, minBoxesPerBuilding = 4, maxBoxesPerBuilding = 20, options = {}) {
  if (!buildableAreaCoords || buildableAreaCoords.length < 4) return { buildPaths: [], boxes: [] };
  
  const { 
    layoutMode = 'density', 
    startingCorner = 'SW', 
    precisionBoxCount = null, 
    precisionOffset = 0,
    precisionOffsetY = 0 
  } = options;
  
  // PRECISION MODE: Boxes centered on orientation line
  if (layoutMode === 'precision') {
    return generatePrecisionLayout(
      buildableAreaCoords, 
      boxWidthFt, 
      boxLengthFt, 
      orientationAngle, 
      precisionBoxCount, 
      precisionOffset,
      precisionOffsetY,
      maxBoxesPerBuilding
    );
  }
  
  // DENSITY MODE: Fill parcel from corner (existing logic with corner support)
  
  // Calculate buildable area metrics for strategy validation
  const buildablePoly = turf.polygon([buildableAreaCoords]);
  const buildableArea = turf.area(buildablePoly);
  const boxAreaM2 = (boxWidthFt * 0.3048) * (boxLengthFt * 0.3048);
  const theoreticalMaxBoxes = Math.floor(buildableArea / boxAreaM2);
  
  // More flexible efficiency threshold for different parcel sizes
  let efficiencyThreshold;
  if (theoreticalMaxBoxes <= 5) {
    efficiencyThreshold = Math.max(1, minBoxesPerBuilding);
  } else if (theoreticalMaxBoxes <= 15) {
    efficiencyThreshold = Math.max(2, theoreticalMaxBoxes * 0.10);
  } else {
    efficiencyThreshold = Math.max(minBoxesPerBuilding, theoreticalMaxBoxes * 0.15);
  }

  // Helper function to test a strategy and return results with efficiency metrics
  function testStrategy(strategyId, angle) {
    const result = generateLayoutForStrategy(strategyId, buildableAreaCoords, boxWidthFt, boxLengthFt, buildingPaddingFt, angle, minBoxesPerBuilding, maxBoxesPerBuilding, startingCorner);
    // Ensure result arrays are safe
    const safeResultBuildPaths = Array.isArray(result?.buildPaths) ? result.buildPaths : [];
    const safeResultBoxes = Array.isArray(result?.boxes) ? result.boxes : [];
    const efficiency = safeResultBoxes.length / theoreticalMaxBoxes;
    const validBuildings = safeResultBuildPaths.filter(building => {
      const buildingBoxes = safeResultBoxes.filter(box => box.buildPathId === building.id);
      return buildingBoxes.length >= minBoxesPerBuilding && buildingBoxes.length <= maxBoxesPerBuilding;
    }).length;
    
    return {
      ...result,
      buildPaths: safeResultBuildPaths,
      boxes: safeResultBoxes,
      efficiency,
      validBuildings,
      totalBoxes: safeResultBoxes.length,
      strategyUsed: strategyId,
      angleUsed: angle
    };
  }

  // Smart strategy selection with validation
  if (strategy === 'smart-hybrid') {
    const isManualOrientation = orientationAngle !== 0;
    const anglesToTest = isManualOrientation ? 
      [orientationAngle] : 
      [orientationAngle, orientationAngle + 90, orientationAngle + 45, orientationAngle - 45];
    
    const bounds = turf.bbox(turf.polygon([buildableAreaCoords]));
    const [minLng, minLat, maxLng, maxLat] = bounds;
    const widthDeg = maxLng - minLng;
    const heightDeg = maxLat - minLat;
    const aspectRatio = widthDeg / heightDeg;
    const strategies = [
      { id: 'parallel-to-orientation', angles: [orientationAngle], priority: 1 },
      { id: 'perpendicular-to-orientation', angles: [orientationAngle], priority: 1 }
    ];
    
    let bestResult = { boxes: [], buildPaths: [], efficiency: 0, totalBoxes: 0 };
    
    for (const strat of strategies.sort((a, b) => a.priority - b.priority)) {
      for (const angle of strat.angles) {
        const result = testStrategy(strat.id, angle);
        if (result.totalBoxes > bestResult.totalBoxes || 
           (result.totalBoxes === bestResult.totalBoxes && result.efficiency > bestResult.efficiency)) {
          bestResult = result;
          if (strat.priority <= 2 && result.totalBoxes >= Math.max(3, efficiencyThreshold * 0.5)) {
            break;
          }
        }
      }
      if (bestResult.totalBoxes >= efficiencyThreshold) break;
    }
    
    return bestResult.totalBoxes > 0 ? bestResult : { buildPaths: [], boxes: [], layoutAngle: orientationAngle };
  }

  // Test the requested strategy
  const primaryResult = testStrategy(strategy, orientationAngle);
  
  // For EXPLICIT strategy choices (parallel or perpendicular), respect the user's choice
  if (strategy === 'parallel-to-orientation' || strategy === 'perpendicular-to-orientation') {
    if (primaryResult.totalBoxes > 0) {
      return primaryResult;
    }
    
    // Only if zero boxes, try minor angle adjustments
    const microAdjustments = [1, -1, 2, -2, 3, -3, 5, -5];
    for (const adj of microAdjustments) {
      const adjustedResult = testStrategy(strategy, orientationAngle + adj);
      if (adjustedResult.totalBoxes > 0) {
        return adjustedResult;
      }
    }
    
    return { buildPaths: [], boxes: [], layoutAngle: orientationAngle };
  }
  
  // Legacy fallback
  return primaryResult.totalBoxes > 0 ? primaryResult : { buildPaths: [], boxes: [], layoutAngle: orientationAngle };
}

// SIMPLIFIED LAYOUT - Rotate coordinate system, place axis-aligned boxes, rotate back
// This keeps the math simple and predictable

function generateLayoutForStrategy(strategy, buildableAreaCoords, boxWidthFt, boxLengthFt, buildingPaddingFt = 0, orientationAngle = 0, minBoxesPerBuilding = 4, maxBoxesPerBuilding = 20, startingCorner = 'SW') {
  if (!buildableAreaCoords || buildableAreaCoords.length < 4) return { buildPaths: [], boxes: [] };
  
  // Convert dimensions to meters
  const boxLengthM = boxLengthFt * 0.3048;  // 68ft - long dimension
  const boxWidthM = boxWidthFt * 0.3048;    // 12.9ft - short dimension
  const buildingSpacingM = buildingPaddingFt * 0.3048;
  
  // Calculate centroid
  const centroid = [
    buildableAreaCoords.reduce((a, b) => a + b[0], 0) / buildableAreaCoords.length,
    buildableAreaCoords.reduce((a, b) => a + b[1], 0) / buildableAreaCoords.length
  ];
  
  // Convert buildable area to meters
  const polyMeters = buildableAreaCoords.map(pt => lngLatToMeters(pt, centroid));
  const buildablePoly = turf.polygon([buildableAreaCoords]);
  
  // Rotation helper
  function rotatePt([x, y], angleDeg) {
    const angleRad = angleDeg * Math.PI / 180;
    return [
      x * Math.cos(angleRad) - y * Math.sin(angleRad),
      x * Math.sin(angleRad) + y * Math.cos(angleRad)
    ];
  }
  
  // Determine the rotation angle for the coordinate system
  // In rotated coords: X = build path (boxes placed side-by-side), Y = box length direction
  // 
  // The strategy names refer to how the BUILDING ROW relates to the orientation line:
  // 
  // PARALLEL to Orientation = building row runs ALONG the line
  //   - Build path (X axis) should be at orientationAngle
  //   - Box LENGTH (Y axis) is perpendicular to row, at orientationAngle + 90
  //   - rotationAngle = orientationAngle
  //
  // PERPENDICULAR to Orientation = building row CROSSES the line
  //   - Build path (X axis) should be at orientationAngle + 90
  //   - Box LENGTH (Y axis) runs along the line, at orientationAngle + 180 = orientationAngle
  //   - rotationAngle = orientationAngle + 90
  //
  const isPerpendicular = strategy === 'perpendicular-to-orientation';
  const rotationAngle = isPerpendicular ? (orientationAngle + 90) : orientationAngle;
  
  console.log(`📐 LAYOUT: orientation=${orientationAngle?.toFixed(1)}°, rotation=${rotationAngle.toFixed(1)}°, mode=${isPerpendicular ? 'PERP' : 'PARALLEL'}, corner=${startingCorner}`);
  console.log(`📐 Box: ${boxWidthFt}ft(W) × ${boxLengthFt}ft(L) = ${boxWidthM.toFixed(2)}m × ${boxLengthM.toFixed(2)}m, spacing=${buildingPaddingFt}ft`);
  
  // Rotate buildable polygon to align with our coordinate system
  // In rotated coords: X = build path direction, Y = row direction
  const rotatedPoly = polyMeters.map(pt => rotatePt(pt, -rotationAngle));
  
  // Get bounds in rotated coordinate system
  const xs = rotatedPoly.map(c => c[0]);
  const ys = rotatedPoly.map(c => c[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  
  console.log(`📐 Bounds (rotated): X=[${minX.toFixed(1)}, ${maxX.toFixed(1)}], Y=[${minY.toFixed(1)}, ${maxY.toFixed(1)}]`);
  
  // In rotated coords:
  // - Boxes are placed with WIDTH along X (build path) and LENGTH along Y
  // - Each box is boxWidthM wide (X) and boxLengthM tall (Y)
  // - Boxes are placed adjacent along X (side-by-side)
  // - Rows advance along Y
  
  const rowHeight = boxLengthM + buildingSpacingM;
  
  // Check if a box at (x, y) corner is valid (all 4 corners inside buildable)
  function isBoxValid(x, y) {
    // Box corners in rotated coords (x, y is bottom-left corner)
    const corners = [
      [x, y],
      [x + boxWidthM, y],
      [x + boxWidthM, y + boxLengthM],
      [x, y + boxLengthM]
    ];
    
    // Rotate back to world meters, convert to lng/lat, check containment
    for (const corner of corners) {
      const worldMeters = rotatePt(corner, rotationAngle);
      const lngLat = metersToLngLat(worldMeters, centroid);
      try {
        if (!turf.booleanPointInPolygon(lngLat, buildablePoly)) {
          return false;
        }
      } catch (e) {
        return false;
      }
    }
    return true;
  }
  
  // Determine starting position and direction based on corner
  let startX, startY, dirX, dirY;
  switch (startingCorner) {
    case 'SW': startX = minX; startY = minY; dirX = 1; dirY = 1; break;
    case 'SE': startX = maxX - boxWidthM; startY = minY; dirX = -1; dirY = 1; break;
    case 'NW': startX = minX; startY = maxY - boxLengthM; dirX = 1; dirY = -1; break;
    case 'NE': startX = maxX - boxWidthM; startY = maxY - boxLengthM; dirX = -1; dirY = -1; break;
    default: startX = minX; startY = minY; dirX = 1; dirY = 1;
  }
  
  console.log(`📐 Bounds: X=[${minX.toFixed(1)}, ${maxX.toFixed(1)}] (${((maxX-minX)/0.3048).toFixed(0)}ft), Y=[${minY.toFixed(1)}, ${maxY.toFixed(1)}] (${((maxY-minY)/0.3048).toFixed(0)}ft)`);
  console.log(`📐 Start: (${startX.toFixed(1)}, ${startY.toFixed(1)}), dir: (${dirX}, ${dirY}), rowHeight=${rowHeight.toFixed(1)}m`);
  console.log(`📐 Max boxes along X: ~${Math.floor((maxX - minX) / boxWidthM)}, rows along Y: ~${Math.floor((maxY - minY) / rowHeight)}`);
  
  const buildPaths = [];
  const allBoxes = [];
  let buildingId = 1;
  
  // Place rows - scan the ENTIRE row to find all valid positions
  let rowY = startY;
  const maxRows = 50;
  
  for (let rowNum = 0; rowNum < maxRows; rowNum++) {
    // Check if row is still in bounds
    if (dirY > 0 && rowY > maxY - boxLengthM) break;
    if (dirY < 0 && rowY < minY) break;
    
    // Scan entire row to find ALL valid box positions
    const validPositions = [];
    let boxX = minX; // Always scan from minX to maxX
    
    while (boxX <= maxX - boxWidthM) {
      if (isBoxValid(boxX, rowY)) {
        validPositions.push({ x: boxX, y: rowY });
      }
      boxX += boxWidthM;
    }
    
    if (validPositions.length > 0) {
      console.log(`  📊 Row ${rowNum}: found ${validPositions.length} valid positions at Y=${rowY.toFixed(1)}`);
    }
    
    // Group consecutive valid positions into buildings
    let currentBuilding = [];
    for (let i = 0; i < validPositions.length; i++) {
      const pos = validPositions[i];
      
      if (currentBuilding.length === 0) {
        currentBuilding.push(pos);
      } else {
        const lastPos = currentBuilding[currentBuilding.length - 1];
        const gap = Math.abs(pos.x - lastPos.x);
        
        // If this position is adjacent to the last one (within tolerance)
        if (gap < boxWidthM * 1.5) {
          currentBuilding.push(pos);
        } else {
          // Gap detected - save current building if big enough, start new one
          if (currentBuilding.length >= minBoxesPerBuilding) {
            const buildingBoxes = currentBuilding.slice(0, maxBoxesPerBuilding).map((p, idx) => ({
              x: p.x, y: p.y, id: idx + 1
            }));
            
            // Convert and add building (code below will handle this)
            const worldBoxes = buildingBoxes.map((box, idx) => {
              const corners = [
                [box.x, box.y],
                [box.x + boxWidthM, box.y],
                [box.x + boxWidthM, box.y + boxLengthM],
                [box.x, box.y + boxLengthM],
                [box.x, box.y]
              ];
              const worldCorners = corners.map(c => {
                const worldMeters = rotatePt(c, rotationAngle);
                return metersToLngLat(worldMeters, centroid);
              });
              return { id: idx + 1, buildPathId: buildingId, corners: worldCorners };
            });
            
            buildPaths.push({ id: buildingId, boxes: worldBoxes });
            worldBoxes.forEach(box => allBoxes.push(box));
            console.log(`🏢 Building ${buildingId}: ${worldBoxes.length} boxes at rowY=${rowY.toFixed(1)}m`);
            buildingId++;
          }
          currentBuilding = [pos];
        }
      }
    }
    
    // Don't forget the last building segment in this row
    if (currentBuilding.length >= minBoxesPerBuilding) {
      const buildingBoxes = currentBuilding.slice(0, maxBoxesPerBuilding).map((p, idx) => ({
        x: p.x, y: p.y, id: idx + 1
      }));
      
      // Convert boxes to world coordinates with corners
      const worldBoxes = buildingBoxes.map((box, idx) => {
        const corners = [
          [box.x, box.y],
          [box.x + boxWidthM, box.y],
          [box.x + boxWidthM, box.y + boxLengthM],
          [box.x, box.y + boxLengthM],
          [box.x, box.y] // close polygon
        ];
        
        const worldCorners = corners.map(c => {
          const worldMeters = rotatePt(c, rotationAngle);
          return metersToLngLat(worldMeters, centroid);
        });
        
        return {
          id: idx + 1,
          buildPathId: buildingId,
          corners: worldCorners
        };
      });
      
      buildPaths.push({ id: buildingId, boxes: worldBoxes });
      worldBoxes.forEach(box => allBoxes.push(box));
      
      console.log(`🏢 Building ${buildingId}: ${worldBoxes.length} boxes at rowY=${rowY.toFixed(1)}m`);
      buildingId++;
    }
    
    // Move to next row
    rowY += dirY * rowHeight;
  }
  
  console.log(`✅ Generated ${buildPaths.length} buildings, ${allBoxes.length} boxes`);
  
  return { buildPaths, boxes: allBoxes, layoutAngle: orientationAngle };
}


// Export the generatePCALayout function for use by ModIQ optimizer
export { generatePCALayout, getInsetPolygon };

const MapboxPCAVisualizer = ({ 
  onParcelSelected, 
  onBuildingDataUpdate, 
  layoutStrategy: externalLayoutStrategy, 
  selectedBuildingId, 
  selectedOptimizations, 
  buildingOptimizations,
  floorCount = 1,
  setFloorCount = null,
  hasPodium = false,
  setHasPodium = null,
  optimalConfiguration = null,
  onConfigurationApplied = null,
  orientationOverride = null,
  setOrientationOverride = null,
  onPrincipalAngleUpdate = null,
  showSettingsPanel = false,
  setShowSettingsPanel = null,
  buildPaths: externalBuildPaths = null,
  boxes: externalBoxes = null,
  onLogout = null, // Add logout prop
  // NEW: Accept external parcel data for project loading
  externalParcelCoordinates = null,
  externalSetbackPoints = null,
  externalSetbackDistances = null,
  // Callback to report setback distances changes for saving
  onSetbackDistancesChange = null,
  // Callback when user selects an address from the search
  onAddressSelected = null,
  // Project address for settings panel
  projectAddress = '',
  onProjectAddressChange = null,
  // Layout mode: 'density' (fill parcel) or 'precision' (centered on line)
  layoutMode = 'density',
  // Density mode: starting corner for fill direction (SW, SE, NW, NE)
  startingCorner = 'SW',
  // Precision mode: specific box count (null = auto-fill to boundaries)
  precisionBoxCount = null,
  // Precision mode: X-axis offset perpendicular to orientation line in feet
  precisionOffset = 0,
  // Precision mode: Y-axis offset along orientation line in feet
  precisionOffsetY = 0
}) => {
  const [parcelCoords, setParcelCoords] = useState(null);
  const [axisLine, setAxisLine] = useState(null);
  const [principalAngle, setPrincipalAngle] = useState(null);
  const [setbackDistances, setSetbackDistances] = useState([]);
  const [layoutStrategy, setLayoutStrategy] = useState(settingsManager.get('layout.defaultStrategy'));
  const [boxWidth, setBoxWidth] = useState(settingsManager.get('boxDimensions.width'));
  const [boxLength, setBoxLength] = useState(settingsManager.get('boxDimensions.length'));
  const [buildingPadding, setBuildingPadding] = useState(settingsManager.get('building.padding'));
  const [podiumHeight, setPodiumHeight] = useState(settingsManager.get('building.podiumHeight'));
  
  const [minBoxWidth, setMinBoxWidth] = useState(settingsManager.get('boxConstraints.minWidth'));
  const [maxBoxWidth, setMaxBoxWidth] = useState(settingsManager.get('boxConstraints.maxWidth'));
  const [minBoxLength, setMinBoxLength] = useState(settingsManager.get('boxConstraints.minLength'));
  const [maxBoxLength, setMaxBoxLength] = useState(settingsManager.get('boxConstraints.maxLength'));

  const [minBoxesPerBuilding, setMinBoxesPerBuilding] = useState(settingsManager.get('buildingConstraints.minBoxes', 3));
  const [maxBoxesPerBuilding, setMaxBoxesPerBuilding] = useState(settingsManager.get('buildingConstraints.maxBoxes', 50));

  const [show3D, setShow3D] = useState(false);
  const [showParcelBoundaries, setShowParcelBoundaries] = useState(false);
  const [regridApiToken, setRegridApiToken] = useState('');
  
  // Terrain control states
  const [enableTerrain, setEnableTerrain] = useState(true);
  const [terrainExaggeration, setTerrainExaggeration] = useState(1.2);
  
  // Flag to prevent auto-zoom during user interaction
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [showEstimatedBuildings, setShowEstimatedBuildings] = useState(true);
  
  // TODO: Set to true when MapBox Enterprise is activated
  const [hasEnterpriseAccess, setHasEnterpriseAccess] = useState(false);
  
  // Ref to prevent infinite loops when processing external data
  const processedExternalDataRef = useRef(null);

  // Debug external building data
  useEffect(() => {
    console.log('🏗️ MapboxPCAVisualizer external building data:', {
      externalBuildPaths: externalBuildPaths?.length || 0,
      externalBoxes: externalBoxes?.length || 0,
      externalParcelCoordinates: externalParcelCoordinates?.length || 0,
      externalSetbackDistances: externalSetbackDistances?.length || 0,
      externalSetbackDistancesValues: externalSetbackDistances
    });
  }, [externalBuildPaths, externalBoxes, externalParcelCoordinates, externalSetbackDistances]);

  const activeLayoutStrategy = externalLayoutStrategy || layoutStrategy;

  const getBuildingHeight = () => {
    const floorHeightFt = 12;
    const totalFloors = floorCount + (hasPodium ? 1 : 0);
    return totalFloors * floorHeightFt * 0.3048;
  };

  const getBuildingColor = (isSelected = false) => {
    const totalFloors = floorCount + (hasPodium ? 1 : 0);
    
    if (isSelected) {
      return '#f59e0b'; // Orange for selected
    }
    
    // Default building color should be yellow, not green
    if (totalFloors >= 8) {
      return '#fbbf24'; // Yellow for tall buildings
    } else if (totalFloors >= 4) {
      return '#fbbf24'; // Yellow (was green #22c55e)
    } else {
      return '#fbbf24'; // Yellow (was blue #3b82f6)
    }
  };

  // Settings update functions
  const updateBoxWidth = (value) => {
    setBoxWidth(value);
    settingsManager.set('boxDimensions.width', value);
  };

  const updateBoxLength = (value) => {
    setBoxLength(value);
    settingsManager.set('boxDimensions.length', value);
  };

  const updateBuildingPadding = (value) => {
    setBuildingPadding(value);
    settingsManager.set('building.padding', value);
  };

  const updateMinBoxWidth = (value) => {
    setMinBoxWidth(value);
    settingsManager.set('boxConstraints.minWidth', value);
  };

  const updateMaxBoxWidth = (value) => {
    setMaxBoxWidth(value);
    settingsManager.set('boxConstraints.maxWidth', value);
  };

  const updateMinBoxLength = (value) => {
    setMinBoxLength(value);
    settingsManager.set('boxConstraints.minLength', value);
  };

  const updateMaxBoxLength = (value) => {
    setMaxBoxLength(value);
    settingsManager.set('boxConstraints.maxLength', value);
  };

  const updateMinBoxesPerBuilding = (value) => {
    setMinBoxesPerBuilding(value);
    settingsManager.set('buildingConstraints.minBoxes', value);
  };

  const updateMaxBoxesPerBuilding = (value) => {
    setMaxBoxesPerBuilding(value);
    settingsManager.set('buildingConstraints.maxBoxes', value);
  };

  useEffect(() => {
    if (onPrincipalAngleUpdate && principalAngle !== null) {
      onPrincipalAngleUpdate(principalAngle);
    }
  }, [principalAngle, onPrincipalAngleUpdate]);

  useEffect(() => {
    if (optimalConfiguration && optimalConfiguration.isOptimalConfiguration) {
      console.log('🎯 Applying optimal configuration from ModIQ Pro:', optimalConfiguration);
      
      setLayoutStrategy(optimalConfiguration.layoutStrategy);
      
      if (setOrientationOverride) {
        setOrientationOverride(optimalConfiguration.orientationAngle);
      }
      
      if (onConfigurationApplied) {
        onConfigurationApplied(optimalConfiguration);
      }
    }
  }, [optimalConfiguration, onConfigurationApplied, setOrientationOverride]);

  useEffect(() => {
    const geocoder = document.querySelector('.mapboxgl-ctrl-geocoder');
    if (geocoder) {
      geocoder.style.zIndex = '9999';
      geocoder.style.pointerEvents = 'auto';
      geocoder.style.position = 'relative';
    }
  });

  const handleParcelDrawn = (feature) => {
    if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
      // Set flag to indicate user is interacting (prevent auto-zoom)
      setIsUserInteracting(true);
      
      const coords = feature.geometry.coordinates[0];
      setParcelCoords(coords);
      const angle = calculatePrincipalOrientation(coords);
      setPrincipalAngle(angle);
      const centroid = getCentroid(coords);
      setAxisLine(getAxisLine(centroid, angle));
      const newEdgeCount = coords.length > 1 ? coords.length - 1 : 0;
      setSetbackDistances(prev => {
        if (!prev || prev.length === 0) return Array(newEdgeCount).fill(30);
        if (prev.length === newEdgeCount) return prev;
        if (prev.length > newEdgeCount) return prev.slice(0, newEdgeCount);
        return [...prev, ...Array(newEdgeCount - prev.length).fill(30)];
      });
      if (onParcelSelected) {
        onParcelSelected(coords);
      }
      
      // Reset flag after a short delay to allow for programmatic changes
      setTimeout(() => setIsUserInteracting(false), 1000);
    }
  };

  const buildableAreaCoords = useMemo(() => {
    if (!parcelCoords || !setbackDistances || setbackDistances.length !== parcelCoords.length - 1) {
      console.log('⚠️ Buildable area calculation skipped:', { 
        parcelCoords: parcelCoords?.length || 0, 
        setbackDistances: setbackDistances?.length || 0 
      });
      return null;
    }
    
    const result = getInsetPolygon(parcelCoords, setbackDistances);
    if (result) {
      console.log('✅ Buildable area calculated successfully');
    } else {
      console.warn('❌ Buildable area calculation failed');
    }
    return result;
  }, [parcelCoords, setbackDistances]);

  // Generate layout with setback validation fallback
  const { buildPaths, boxes, actualLayoutAngle } = useMemo(() => {
    const actualAngle = orientationOverride ?? principalAngle;
    console.log(`🔄 LAYOUT: mode=${layoutMode}, angle=${actualAngle?.toFixed(1)}°, strategy=${activeLayoutStrategy}, boxWidth=${boxWidth}ft, boxLength=${boxLength}ft`);
    
    
    // Priority 1: ALWAYS generate internally when we have buildable area
    if (buildableAreaCoords) {
      const result = generatePCALayout(
        activeLayoutStrategy, 
        buildableAreaCoords, 
        boxWidth, 
        boxLength, 
        buildingPadding, 
        actualAngle, 
        minBoxesPerBuilding, 
        maxBoxesPerBuilding,
        {
          layoutMode,
          startingCorner,
          precisionBoxCount,
          precisionOffset,
          precisionOffsetY
        }
      );
      console.log(`✅ Generated: ${result.buildPaths?.length || 0} buildings, ${result.boxes?.length || 0} boxes @ ${result.layoutAngle?.toFixed(1)}°`);
      
      if (result.buildPaths && result.buildPaths.length > 0) {
        return { ...result, actualLayoutAngle: result.layoutAngle };
      } else {
        console.log('⚠️ No buildings generated, using fallback');
        // SETBACK VALIDATION FALLBACK - only use external data if generation fails
        if (externalBuildPaths && externalBoxes && externalBuildPaths.length > 0 && externalBoxes.length > 0) {
          const buildablePoly = turf.polygon([buildableAreaCoords]);
          let validExternalBoxes = [];
          
          for (const box of externalBoxes) {
            if (box.corners && box.corners.length >= 4) {
              try {
                const allCornersInside = box.corners.slice(0, 4).every(corner => {
                  return turf.booleanPointInPolygon(corner, buildablePoly);
                });
                
                if (allCornersInside) {
                  validExternalBoxes.push(box);
                }
              } catch (e) {
                // Skip invalid boxes
              }
            }
          }
          
          const complianceRate = validExternalBoxes.length / externalBoxes.length;
          
          if (complianceRate >= 0.8 && validExternalBoxes.length >= minBoxesPerBuilding) {
            const validBoxesByBuilding = {};
            validExternalBoxes.forEach(box => {
              if (!validBoxesByBuilding[box.buildPathId]) {
                validBoxesByBuilding[box.buildPathId] = [];
              }
              validBoxesByBuilding[box.buildPathId].push(box);
            });
            
            const validBuildingIds = Object.keys(validBoxesByBuilding).filter(buildingId => {
              return validBoxesByBuilding[buildingId].length >= minBoxesPerBuilding && validBoxesByBuilding[buildingId].length <= maxBoxesPerBuilding;
            });
            
            const validBuildPaths = externalBuildPaths.filter(buildPath => 
              validBuildingIds.includes(buildPath.id.toString())
            );
            
            const finalValidBoxes = validExternalBoxes.filter(box => 
              validBuildingIds.includes(box.buildPathId.toString())
            );
            
            if (validBuildPaths.length > 0 && finalValidBoxes.length > 0) {
              console.log('✅ Using setback-compliant external data as fallback');
              return { 
                buildPaths: validBuildPaths, 
                boxes: finalValidBoxes, 
                actualLayoutAngle: actualAngle 
              };
            }
          }
        }
        
        return { buildPaths: [], boxes: [], actualLayoutAngle: actualAngle };
      }
    }
    
    // Priority 2: Use external data if no buildable area available
    if (externalBuildPaths && externalBoxes && externalBuildPaths.length > 0 && externalBoxes.length > 0) {
      console.log('📥 Using external data (no buildable area to generate from)');
      return { buildPaths: externalBuildPaths, boxes: externalBoxes, actualLayoutAngle: principalAngle };
    }
    
    // Priority 3: No data available
    console.log('⚠️ No buildable area or external data available');
    return { buildPaths: [], boxes: [], actualLayoutAngle: principalAngle };
  }, [
    orientationOverride, 
    activeLayoutStrategy, buildableAreaCoords, boxWidth, boxLength, buildingPadding, minBoxesPerBuilding, maxBoxesPerBuilding, principalAngle,
    layoutMode, startingCorner, precisionBoxCount, precisionOffset, precisionOffsetY
  ]);

  // Get display boxes with filtering
  const displayBoxes = useMemo(() => {
    const safeBuildPaths = Array.isArray(buildPaths) ? buildPaths : [];
    const safeBoxes = Array.isArray(boxes) ? boxes : [];
    const minBoxes = minBoxesPerBuilding || 1;
    
    if (!selectedOptimizations || !buildingOptimizations || safeBoxes.length === 0) {
      if (safeBoxes.length === 0 || safeBuildPaths.length === 0) {
        return [];
      }
      
      const validBuildingIds = new Set();
      safeBuildPaths.forEach(buildPath => {
        const buildingBoxes = safeBoxes.filter(box => box.buildPathId === buildPath.id);
        if (buildingBoxes.length >= minBoxes) {
          validBuildingIds.add(buildPath.id);
        }
      });
      
      return safeBoxes.filter(box => validBuildingIds.has(box.buildPathId));
    }

    const optimizedBoxesByBuilding = {};
    
    safeBuildPaths.forEach(buildPath => {
      const buildingId = buildPath.id;
      const optimization = selectedOptimizations[buildingId] || 'none';
      
      if (optimization === 'none') {
        const buildingBoxes = safeBoxes.filter(box => box.buildPathId === buildingId);
        optimizedBoxesByBuilding[buildingId] = buildingBoxes.length >= minBoxes ? buildingBoxes : [];
      } else {
        const buildingOptimization = buildingOptimizations[buildingId];
        if (buildingOptimization && buildingOptimization[optimization]) {
          const optimizedBoxes = buildingOptimization[optimization].optimizedBoxes || [];
          optimizedBoxesByBuilding[buildingId] = optimizedBoxes.length >= minBoxes ? optimizedBoxes : [];
        } else {
          const buildingBoxes = safeBoxes.filter(box => box.buildPathId === buildingId);
          optimizedBoxesByBuilding[buildingId] = buildingBoxes.length >= minBoxes ? buildingBoxes : [];
        }
      }
    });

    return Object.values(optimizedBoxesByBuilding).flat();
  }, [boxes, buildPaths, selectedOptimizations, buildingOptimizations, minBoxesPerBuilding]);

  // Track last synced data to prevent infinite loops
  const lastSyncedRef = useRef({ buildingCount: 0, boxCount: 0 });
  
  // Update parent with building data
  useEffect(() => {
    const safeBuildPathsForUpdate = Array.isArray(buildPaths) ? buildPaths : [];
    const safeBoxesForUpdate = Array.isArray(boxes) ? boxes : [];
    
    if (onBuildingDataUpdate && safeBuildPathsForUpdate.length > 0) {
      const generatedBuildingCount = safeBuildPathsForUpdate.length;
      const generatedBoxCount = safeBoxesForUpdate.length;
      
      // Skip if we already synced this exact data (prevents infinite loop)
      if (lastSyncedRef.current.buildingCount === generatedBuildingCount &&
          lastSyncedRef.current.boxCount === generatedBoxCount) {
        return;
      }
      
      // Track what we're about to sync
      lastSyncedRef.current = { buildingCount: generatedBuildingCount, boxCount: generatedBoxCount };
      
      // Always sync generated data to parent
      {
        const standardBoxes = safeBoxesForUpdate.map(box => ({
          id: box.id,
          buildPathId: box.buildPathId,
          x: box.x || 0,
          y: box.y || 0,
          width: boxWidth * 0.3048,
          height: boxLength * 0.3048,
          orientation: box.orientation,
          corners: box.corners
        }));

        console.log('🔄 Syncing building data to parent:', {
          buildings: generatedBuildingCount,
          boxes: generatedBoxCount
        });

        onBuildingDataUpdate({
          buildPaths: safeBuildPathsForUpdate,
          boxes: standardBoxes,
          layoutStrategy: activeLayoutStrategy,
          boxWidth: boxWidth,
          boxLength: boxLength,
          buildingPadding: buildingPadding,
          orientationAngle: orientationOverride ?? principalAngle,
          buildableAreaCoords: buildableAreaCoords,
          parcelCoords: parcelCoords,
          setbackDistances: setbackDistances,
          minBoxesPerBuilding: minBoxesPerBuilding,
          maxBoxesPerBuilding: maxBoxesPerBuilding
        });
      }
    }
  }, [buildPaths, boxes, activeLayoutStrategy, boxWidth, boxLength, buildingPadding, orientationOverride, principalAngle, buildableAreaCoords, parcelCoords, setbackDistances, onBuildingDataUpdate, minBoxesPerBuilding, maxBoxesPerBuilding]);

  // Force update on orientation change - note: main useEffect above handles sync via ref

  // Axis line calculation - ALWAYS show when we have parcel coords
  const axisLineToShow = useMemo(() => {
    if (!parcelCoords) return null;
    const centroid = getCentroid(parcelCoords);
    // Use orientationOverride first (user's manual setting), then actual layout angle, then principal angle, then 0
    const angle = orientationOverride ?? actualLayoutAngle ?? principalAngle ?? 0;
    if (isNaN(angle)) {
      console.warn('⚠️ Orientation angle is NaN, defaulting to 0');
      return getAxisLine(centroid, 0);
    }
    return getAxisLine(centroid, angle);
  }, [parcelCoords, actualLayoutAngle, orientationOverride, principalAngle]);

  // Overlays
  const overlays = useMemo(() => {
    const arr = [];

    if (axisLineToShow) {
      arr.push({
        id: 'principal-axis',
        data: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: axisLineToShow },
          properties: {}
        },
        type: 'line',
        paint: {
          'line-color': '#e11d48',
          'line-width': 4
        }
      });
    }
    
    if (buildableAreaCoords && buildableAreaCoords.length > 2) {
      arr.push({
        id: 'buildable-area',
        data: {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [buildableAreaCoords] },
          properties: {}
        },
        type: 'fill',
        paint: {
          'fill-color': '#22c55e',
          'fill-opacity': 0.25,
          'fill-outline-color': '#16a34a'
        }
      });
    }

    // Add parcel edge labels with numbers and measurements
    if (parcelCoords && parcelCoords.length > 2 && setbackDistances && setbackDistances.length > 0) {
      const edgeLabels = [];
      const numEdges = parcelCoords.length - 1; // Last point is same as first
      
      for (let i = 0; i < numEdges; i++) {
        const start = parcelCoords[i];
        const end = parcelCoords[i + 1];
        
        // Calculate midpoint of the edge
        const midpoint = [
          (start[0] + end[0]) / 2,
          (start[1] + end[1]) / 2
        ];
        
        // Calculate edge vector and perpendicular (outward normal)
        const edgeVector = [end[0] - start[0], end[1] - start[1]];
        const vectorLength = Math.sqrt(edgeVector[0] ** 2 + edgeVector[1] ** 2);
        
        // Normalized perpendicular vector (90 degrees counterclockwise)
        const perpendicular = [-edgeVector[1] / vectorLength, edgeVector[0] / vectorLength];
        
        // Determine outward direction (away from parcel center)
        const parcelCenter = [
          parcelCoords.reduce((sum, coord) => sum + coord[0], 0) / parcelCoords.length,
          parcelCoords.reduce((sum, coord) => sum + coord[1], 0) / parcelCoords.length
        ];
        
        const toCenter = [parcelCenter[0] - midpoint[0], parcelCenter[1] - midpoint[1]];
        const dotProduct = perpendicular[0] * toCenter[0] + perpendicular[1] * toCenter[1];
        
        // Choose the perpendicular direction that points away from center
        const outwardNormal = dotProduct < 0 ? perpendicular : [-perpendicular[0], -perpendicular[1]];
        
        // Move label position outward by circle diameter distance
        const offsetDistance = 0.0001; // Approximately 11 meters in degrees (about 2 circle diameters)
        const labelPosition = [
          midpoint[0] + outwardNormal[0] * offsetDistance,
          midpoint[1] + outwardNormal[1] * offsetDistance
        ];
        
        // Calculate edge length in feet
        const edgeLength = turf.distance(start, end, { units: 'feet' });
        
        // Create label data for this edge
        edgeLabels.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: labelPosition
          },
          properties: {
            edgeNumber: i + 1,
            edgeLength: Math.round(edgeLength),
            setback: setbackDistances[i] || 0
          }
        });
      }
      
      // Add circle backgrounds for edge labels
      if (edgeLabels.length > 0) {
        arr.push({
          id: 'edge-label-backgrounds',
          data: {
            type: 'FeatureCollection',
            features: edgeLabels
          },
          type: 'circle',
          paint: {
            'circle-radius': 25,
            'circle-color': '#ffffff',
            'circle-opacity': 0.85
          }
        });
        
        // Add edge labels text
        arr.push({
          id: 'edge-labels',
          data: {
            type: 'FeatureCollection',
            features: edgeLabels
          },
          type: 'symbol',
          layout: {
            'text-field': [
              'concat',
              ['to-string', ['get', 'edgeNumber']],
              '\n',
              ['to-string', ['get', 'edgeLength']],
              'ft'
            ],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-anchor': 'center',
            'text-justify': 'center',
            'text-line-height': 1.2,
            'text-allow-overlap': true,
            'text-ignore-placement': true
          },
          paint: {
            'text-color': '#374151',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1
          }
        });
      }
    }
    
    // Wrap box rendering in try-catch so axis line and buildable area always show
    try {
      if (displayBoxes.length > 0) {
        // Filter out boxes with invalid coordinates
        const validBoxes = displayBoxes.filter(box => {
          if (!box.corners || box.corners.length < 4) return false;
          return box.corners.every(corner => {
            if (!Array.isArray(corner) || corner.length < 2) return false;
            const [lng, lat] = corner;
            return !isNaN(lng) && !isNaN(lat) && 
                   Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
          });
        });
        
        if (validBoxes.length !== displayBoxes.length) {
          console.warn(`⚠️ Filtered out ${displayBoxes.length - validBoxes.length} boxes with invalid coordinates`);
        }
        
        if (validBoxes.length === 0) {
          console.log(`📦 No valid boxes to render (all had invalid coordinates)`);
          return arr; // Return what we have (axis line + buildable area)
        }
        
        const selectedBoxes = validBoxes.filter(box => box.buildPathId === selectedBuildingId);
        const unselectedBoxes = validBoxes.filter(box => box.buildPathId !== selectedBuildingId);
        
        console.log(`📦 Boxes: ${validBoxes.length} total, ${selectedBoxes.length} selected (buildingId=${selectedBuildingId}), show3D=${show3D}`);

      const buildingHeightMeters = getBuildingHeight();
      const baseHeight = hasPodium ? podiumHeight * 0.3048 : 0;

      if (show3D) {
        // 3D mode
        
        // Add grey podium layer when podium is enabled
        if (hasPodium && displayBoxes.length > 0) {
          arr.push({
            id: 'podium-layer-3d',
            data: {
              type: 'FeatureCollection',
              features: displayBoxes.map(box => ({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [box.corners ? box.corners : [
                    [box.x, box.y],
                    [box.x + box.width, box.y],
                    [box.x + box.width, box.y + box.height],
                    [box.x, box.y + box.height],
                    [box.x, box.y]
                  ]]
                },
                properties: { 
                  id: `podium-${box.id}`, 
                  buildPathId: box.buildPathId,
                  height: baseHeight, // Height of podium only
                  baseHeight: 0 // Start from ground level
                }
              }))
            },
            type: 'fill-extrusion',
            paint: {
              'fill-extrusion-color': '#6b7280', // Grey color for podium
              'fill-extrusion-height': ['get', 'height'],
              'fill-extrusion-base': ['get', 'baseHeight'],
              'fill-extrusion-opacity': 0.6 // Semi-transparent
            }
          });
        }
        
        if (unselectedBoxes.length > 0) {
          arr.push({
            id: 'pca-boxes-unselected-3d',
            data: {
              type: 'FeatureCollection',
              features: unselectedBoxes.map(box => ({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [box.corners ? box.corners : [
                    [box.x, box.y],
                    [box.x + box.width, box.y],
                    [box.x + box.width, box.y + box.height],
                    [box.x, box.y + box.height],
                    [box.x, box.y]
                  ]]
                },
                properties: { 
                  id: box.id, 
                  buildPathId: box.buildPathId,
                  height: buildingHeightMeters,
                  baseHeight: baseHeight
                }
              }))
            },
            type: 'fill-extrusion',
            paint: {
              'fill-extrusion-color': getBuildingColor(false),
              'fill-extrusion-height': ['get', 'height'],
              'fill-extrusion-base': ['get', 'baseHeight'],
              'fill-extrusion-opacity': 0.85
            }
          });
        }

        if (selectedBoxes.length > 0) {
          arr.push({
            id: 'pca-boxes-selected-3d',
            data: {
              type: 'FeatureCollection',
              features: selectedBoxes.map(box => ({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [box.corners ? box.corners : [
                    [box.x, box.y],
                    [box.x + box.width, box.y],
                    [box.x + box.width, box.y + box.height],
                    [box.x, box.y + box.height],
                    [box.x, box.y]
                  ]]
                },
                properties: { 
                  id: box.id, 
                  buildPathId: box.buildPathId,
                  height: buildingHeightMeters,
                  baseHeight: baseHeight
                }
              }))
            },
            type: 'fill-extrusion',
            paint: {
              'fill-extrusion-color': getBuildingColor(true),
              'fill-extrusion-height': ['get', 'height'],
              'fill-extrusion-base': ['get', 'baseHeight'],
              'fill-extrusion-opacity': 0.9
            }
          });
          
          // Add dimension label for selected building (3D mode)
          try {
            const allCorners = selectedBoxes.flatMap(box => box.corners ? box.corners.slice(0, 4) : []);
            if (allCorners.length > 0) {
              const centerLng = allCorners.reduce((sum, c) => sum + c[0], 0) / allCorners.length;
              const centerLat = allCorners.reduce((sum, c) => sum + c[1], 0) / allCorners.length;
              
              const buildingLengthFt = selectedBoxes.length * boxWidth;
              const buildingWidthFt = boxLength;
              const floorHeightFt = 10;
              const buildingHeightFt = floorCount * floorHeightFt;
              
              const dimensionText = `${buildingLengthFt.toFixed(0)}' × ${buildingWidthFt.toFixed(0)}' × ${buildingHeightFt.toFixed(0)}'`;
              
              console.log(`📏 3D Dimension label: ${dimensionText} at [${centerLng.toFixed(6)}, ${centerLat.toFixed(6)}]`);
              
              arr.push({
                id: 'building-dimensions-label-3d',
                data: {
                  type: 'FeatureCollection',
                  features: [{
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: [centerLng, centerLat]
                    },
                    properties: { label: dimensionText }
                  }]
                },
                type: 'symbol',
                layout: {
                  'text-field': ['get', 'label'],
                  'text-size': 16,
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-anchor': 'center',
                  'text-allow-overlap': true,
                  'text-ignore-placement': true,
                  'text-offset': [0, -3]  // Offset upward to appear above the building
                },
                paint: {
                  'text-color': '#ffffff',
                  'text-halo-color': '#1f2937',
                  'text-halo-width': 2
                }
              });
            }
          } catch (labelError) {
            console.warn('Could not add 3D dimension label:', labelError);
          }
        }
      } else {
        // 2D mode
        if (unselectedBoxes.length > 0) {
          arr.push({
            id: 'pca-boxes-unselected-2d',
            data: {
              type: 'FeatureCollection',
              features: unselectedBoxes.map(box => ({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [box.corners ? box.corners : [
                    [box.x, box.y],
                    [box.x + box.width, box.y],
                    [box.x + box.width, box.y + box.height],
                    [box.x, box.y + box.height],
                    [box.x, box.y]
                  ]]
                },
                properties: { id: box.id, buildPathId: box.buildPathId }
              }))
            },
            type: 'fill',
            paint: {
              'fill-color': getBuildingColor(false),
              'fill-opacity': 0.6,
              'fill-outline-color': '#1e40af'
            }
          });
        }

        if (selectedBoxes.length > 0) {
          arr.push({
            id: 'pca-boxes-selected-2d',
            data: {
              type: 'FeatureCollection',
              features: selectedBoxes.map(box => ({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [box.corners ? box.corners : [
                    [box.x, box.y],
                    [box.x + box.width, box.y],
                    [box.x + box.width, box.y + box.height],
                    [box.x, box.y + box.height],
                    [box.x, box.y]
                  ]]
                },
                properties: { id: box.id, buildPathId: box.buildPathId }
              }))
            },
            type: 'fill',
            paint: {
              'fill-color': getBuildingColor(true),
              'fill-opacity': 0.8,
              'fill-outline-color': '#d97706'
            }
          });
          
          // Add dimension label for selected building (2D mode)
          try {
            const allCorners = selectedBoxes.flatMap(box => box.corners ? box.corners.slice(0, 4) : []);
            if (allCorners.length > 0) {
              const centerLng = allCorners.reduce((sum, c) => sum + c[0], 0) / allCorners.length;
              const centerLat = allCorners.reduce((sum, c) => sum + c[1], 0) / allCorners.length;
              
              const buildingLengthFt = selectedBoxes.length * boxWidth;
              const buildingWidthFt = boxLength;
              const floorHeightFt = 10;
              const buildingHeightFt = floorCount * floorHeightFt;
              
              const dimensionText = `${buildingLengthFt.toFixed(0)}' × ${buildingWidthFt.toFixed(0)}' × ${buildingHeightFt.toFixed(0)}'`;
              
              console.log(`📏 2D Dimension label: ${dimensionText} at [${centerLng.toFixed(6)}, ${centerLat.toFixed(6)}]`);
              
              arr.push({
                id: 'building-dimensions-label-2d',
                data: {
                  type: 'FeatureCollection',
                  features: [{
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: [centerLng, centerLat]
                    },
                    properties: { label: dimensionText }
                  }]
                },
                type: 'symbol',
                layout: {
                  'text-field': ['get', 'label'],
                  'text-size': 16,
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-anchor': 'center',
                  'text-allow-overlap': true,
                  'text-ignore-placement': true
                },
                paint: {
                  'text-color': '#1f2937',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2
                }
              });
            }
          } catch (labelError) {
            console.warn('Could not add 2D dimension label:', labelError);
          }
        }
      }
    }
    } catch (boxRenderError) {
      console.error('❌ Error rendering boxes (axis line + buildable area still shown):', boxRenderError);
    }
    
    return arr;
  }, [axisLineToShow, buildableAreaCoords, displayBoxes, selectedBuildingId, show3D, floorCount, hasPodium, podiumHeight, enableTerrain, boxWidth, boxLength]);

  const handleSetbacksChange = (newDistances) => {
    console.log('🎛️ Setback distances changed via UI:', newDistances);
    console.log('🎛️ Previous setback distances:', setbackDistances);
    setSetbackDistances(newDistances);
    if (onSetbackDistancesChange) {
      console.log('🎛️ Notifying parent of UI setback change');
      onSetbackDistancesChange(newDistances);
    }
  };

  // NEW: Handle external parcel coordinates (for project loading)
  useEffect(() => {
    if (externalParcelCoordinates && Array.isArray(externalParcelCoordinates)) {
      // Create a unique key for this external data to prevent re-processing
      const dataKey = JSON.stringify({
        coords: externalParcelCoordinates,
        setbacks: externalSetbackDistances,
        points: externalSetbackPoints
      });
      
      // Skip if we've already processed this exact data
      if (processedExternalDataRef.current === dataKey) {
        console.log('🔄 Skipping duplicate external data processing');
        return;
      }
      
      console.log('🗺️ Loading external parcel coordinates:', externalParcelCoordinates);
      
      // Mark this data as processed
      processedExternalDataRef.current = dataKey;
      
      // Set parcel coordinates
      setParcelCoords(externalParcelCoordinates);
      
      // Calculate principal angle from loaded coordinates
      const angle = calculatePrincipalOrientation(externalParcelCoordinates);
      setPrincipalAngle(angle);
      
      // Set up axis line
      const centroid = getCentroid(externalParcelCoordinates);
      setAxisLine(getAxisLine(centroid, angle));
      
      // Set up setback distances
      const newEdgeCount = externalParcelCoordinates.length > 1 ? externalParcelCoordinates.length - 1 : 0;
      
      // Use external setback distances if available, otherwise default setbacks
      if (externalSetbackDistances && Array.isArray(externalSetbackDistances) && externalSetbackDistances.length > 0) {
        console.log('🗺️ Loading external setback distances:', externalSetbackDistances);
        console.log('🗺️ External setback distances length:', externalSetbackDistances.length);
        console.log('🗺️ New edge count needed:', newEdgeCount);
        
        // If we have fewer setback distances than edges, fill remaining with the last value or 30
        const actualSetbacks = [];
        for (let i = 0; i < newEdgeCount; i++) {
          if (i < externalSetbackDistances.length) {
            actualSetbacks.push(externalSetbackDistances[i] || 30);
          } else {
            // Use the last available setback value or default to 30
            actualSetbacks.push(externalSetbackDistances[externalSetbackDistances.length - 1] || 30);
          }
        }
        
        console.log('📏 Applied setback distances to edges:', actualSetbacks);
        setSetbackDistances(actualSetbacks);
        
        // Notify parent component of the setback distances
        if (onSetbackDistancesChange) {
          console.log('🔄 Notifying parent of setback distance change');
          onSetbackDistancesChange(actualSetbacks);
        }
      } else {
        console.log('⚠️ External setback distances not valid:', {
          externalSetbackDistances,
          isArray: Array.isArray(externalSetbackDistances),
          length: externalSetbackDistances?.length,
          condition1: !!externalSetbackDistances,
          condition2: Array.isArray(externalSetbackDistances),
          condition3: externalSetbackDistances?.length > 0,
          finalCondition: !!(externalSetbackDistances && Array.isArray(externalSetbackDistances) && externalSetbackDistances.length > 0)
        });
        
        if (externalSetbackPoints && Array.isArray(externalSetbackPoints)) {
          console.log('🗺️ No setback distances provided, using default 30ft setbacks');
          const defaultSetbacks = Array(newEdgeCount).fill(30);
          setSetbackDistances(defaultSetbacks);
          
          // Notify parent component
          if (onSetbackDistancesChange) {
            console.log('🔄 Notifying parent of default setback distances');
            onSetbackDistancesChange(defaultSetbacks);
          }
        } else {
          console.log('🗺️ No setback data provided, using default 30ft setbacks');
          const defaultSetbacks = Array(newEdgeCount).fill(30);
          setSetbackDistances(defaultSetbacks);
          
          // Notify parent component
          if (onSetbackDistancesChange) {
            console.log('🔄 Notifying parent of default setback distances');
            onSetbackDistancesChange(defaultSetbacks);
          }
        }
      }
      
      // Auto-zoom to parcel location with better map access (only if not user interacting)
      if (!isUserInteracting) {
        setTimeout(() => {
          try {
          const bounds = externalParcelCoordinates.reduce((bounds, coord) => {
            return [
              [Math.min(coord[0], bounds[0][0]), Math.min(coord[1], bounds[0][1])], // SW
              [Math.max(coord[0], bounds[1][0]), Math.max(coord[1], bounds[1][1])]  // NE
            ];
          }, [[Infinity, Infinity], [-Infinity, -Infinity]]);
          
          // Add padding to bounds
          const padding = 0.001; // Adjust as needed
          const paddedBounds = [
            [bounds[0][0] - padding, bounds[0][1] - padding],
            [bounds[1][0] + padding, bounds[1][1] + padding]
          ];
          
          console.log('🎯 Auto-zooming to parcel bounds:', paddedBounds);
          
          // Try multiple methods to find the map instance
          const mapContainer = document.querySelector('.mapboxgl-map');
          const mapCanvas = document.querySelector('.mapboxgl-canvas');
          
          let mapInstance = null;
          if (mapContainer && mapContainer._map) {
            mapInstance = mapContainer._map;
          } else if (mapCanvas && mapCanvas.parentElement && mapCanvas.parentElement._map) {
            mapInstance = mapCanvas.parentElement._map;
          } else {
            // Try to find map through mapboxgl global
            const allMaps = document.querySelectorAll('[class*="mapbox"]');
            for (let i = 0; i < allMaps.length; i++) {
              if (allMaps[i]._map) {
                mapInstance = allMaps[i]._map;
                break;
              }
            }
          }
          
          if (mapInstance) {
            console.log('✅ Found map instance, zooming to parcel');
            mapInstance.fitBounds(paddedBounds, { 
              padding: 50,
              maxZoom: 18 
            });
          } else {
            console.warn('⚠️ Could not find map instance for auto-zoom');
          }
        } catch (error) {
          console.error('❌ Error during auto-zoom:', error);
        }
      }, 1000); // Increased delay to ensure map is fully ready
      } else {
        console.log('🚫 Auto-zoom skipped - user is interacting with parcel');
      }
      
      // Notify parent component
      if (onParcelSelected) {
        console.log('📞 Calling onParcelSelected with coordinates');
        onParcelSelected(externalParcelCoordinates);
      } else {
        console.log('⚠️ No onParcelSelected callback provided');
      }
    }
  }, [externalParcelCoordinates, externalSetbackPoints, externalSetbackDistances, onParcelSelected, onSetbackDistancesChange, isUserInteracting]);

  // Separate useEffect for handling setback distance changes independent of parcel coordinates
  // TEMPORARILY DISABLED to debug conflicts
  /*
  useEffect(() => {
    if (parcelCoords && externalSetbackDistances && Array.isArray(externalSetbackDistances) && externalSetbackDistances.length > 0) {
      // Only process if this is truly independent (not part of initial parcel loading)
      const dataKey = JSON.stringify(externalSetbackDistances);
      if (processedExternalDataRef.current && processedExternalDataRef.current.includes(dataKey)) {
        console.log('🔄 Skipping duplicate independent setback processing');
        return;
      }
      
      console.log('🔄 Processing external setback distance changes independently:', externalSetbackDistances);
      
      const newEdgeCount = parcelCoords.length > 1 ? parcelCoords.length - 1 : 0;
      
      // Apply the external setback distances to the existing parcel
      const actualSetbacks = [];
      for (let i = 0; i < newEdgeCount; i++) {
        if (i < externalSetbackDistances.length) {
          actualSetbacks.push(externalSetbackDistances[i] || 30);
        } else {
          actualSetbacks.push(externalSetbackDistances[externalSetbackDistances.length - 1] || 30);
        }
      }
      
      console.log('🔄 Applying independent setback distances:', actualSetbacks);
      setSetbackDistances(actualSetbacks);
      
      // Don't notify parent to avoid circular updates
      console.log('🔄 Independent setback processing complete');
    }
  }, [externalSetbackDistances, parcelCoords]);
  */

  // 🏔️ Terrain functionality available but simplified for stability
  const handleMapLoad = useCallback((map) => {
    console.log('🗺️ Map loaded with terrain support:', !!map.getTerrain());
  }, []);


  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)', // Center horizontally
          zIndex: 10,
          backgroundColor: 'white',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          gap: '2px'
        }}>
          <button
            onClick={() => setShow3D(!show3D)}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: show3D ? '#3b82f6' : 'white',
              color: show3D ? 'white' : '#374151',
              borderRadius: '6px 0 0 6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            title={show3D ? 'Switch to 2D view' : 'Switch to 3D view'}
          >
            {show3D ? '3D' : '2D'}
          </button>
          
          <button
            onClick={() => setEnableTerrain(!enableTerrain)}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: enableTerrain ? '#16a34a' : '#f3f4f6',
              color: enableTerrain ? 'white' : '#374151',
              borderRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            title={enableTerrain ? 'Disable 3D terrain' : 'Enable 3D terrain'}
          >
            🏔️ {enableTerrain ? 'ON' : 'OFF'}
          </button>
          
          <button
            onClick={() => setShowEstimatedBuildings(!showEstimatedBuildings)}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: showEstimatedBuildings ? '#0ea5e9' : '#f3f4f6',
              color: showEstimatedBuildings ? 'white' : '#374151',
              borderRadius: '0 6px 6px 0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            title={showEstimatedBuildings ? 'Hide estimated building heights' : 'Show estimated building heights'}
          >
            🏢 {showEstimatedBuildings ? 'ON' : 'OFF'}
          </button>
        </div>

        <PCASettingsPanel
          open={showSettingsPanel}
          onClose={() => setShowSettingsPanel && setShowSettingsPanel(false)}
          parcelCoords={parcelCoords}
          setbackDistances={setbackDistances}
          setSetbackDistances={setSetbackDistances}
          onSetbacksChange={handleSetbacksChange}
          buildingPadding={buildingPadding}
          setBuildingPadding={updateBuildingPadding}
          floorCount={floorCount}
          setFloorCount={setFloorCount}
          hasPodium={hasPodium}
          setHasPodium={setHasPodium}
          podiumHeight={podiumHeight}
          setPodiumHeight={setPodiumHeight}
          minBoxesPerBuilding={minBoxesPerBuilding}
          setMinBoxesPerBuilding={updateMinBoxesPerBuilding}
          maxBoxesPerBuilding={maxBoxesPerBuilding}
          setMaxBoxesPerBuilding={updateMaxBoxesPerBuilding}
          boxWidth={boxWidth}
          setBoxWidth={updateBoxWidth}
          boxLength={boxLength}
          setBoxLength={updateBoxLength}
          minBoxWidth={minBoxWidth}
          setMinBoxWidth={updateMinBoxWidth}
          maxBoxWidth={maxBoxWidth}
          setMaxBoxWidth={updateMaxBoxWidth}
          minBoxLength={minBoxLength}
          setMinBoxLength={updateMinBoxLength}
          maxBoxLength={maxBoxLength}
          setMaxBoxLength={updateMaxBoxLength}
          showParcelBoundaries={showParcelBoundaries}
          onShowParcelBoundariesChange={setShowParcelBoundaries}
          regridApiToken={regridApiToken}
          onRegridApiTokenChange={setRegridApiToken}
          projectAddress={projectAddress}
          onProjectAddressChange={onProjectAddressChange}
          onLogout={onLogout} // Pass onLogout prop
        />
        <MapboxMap 
          onParcelDrawn={handleParcelDrawn} 
          overlays={overlays} 
          externalParcelCoordinates={externalParcelCoordinates}
          enableTerrain={enableTerrain}
          terrainExaggeration={terrainExaggeration}
          onMapLoad={handleMapLoad}
          onAddressSelected={onAddressSelected}
        />
      </div>
    </div>
  );
};

export default MapboxPCAVisualizer;
